import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { useMediaQuery } from "react-responsive";

// Role → themeable pill / avatar tint
const ROLE_TINT = {
  buyer: { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
  supplier: { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  offline_buyer: {
    bg: "var(--color-surface-2)",
    color: "var(--color-text-secondary)",
  },
  offline_supplier: {
    bg: "var(--color-surface-2)",
    color: "var(--color-text-secondary)",
  },
  subadmin: { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
  admin: { bg: "var(--color-danger-bg)", color: "var(--color-danger-text)" },
  default: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
};
const tint = (role) => ROLE_TINT[role] || ROLE_TINT.default;
const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const NewRegistration = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  let is_alert = false;
  const user = localStorage.getItem("user");
  const userData = JSON?.parse(user);
  const userType = userData?.user_type;

  useEffect(() => {
    if (location.state !== null) {
      if (is_alert === false) {
        toast.success(location.state);
      }
      is_alert = true;
    }
    window.history.replaceState({}, location.state);
  }, []);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [myOrderFilter, setMyOrderFilter] = useState("All");
  const [monthFilter, setMonthFilter] = useState("");
  const [buyerId, setBuyerId] = useState(() => {
    return localStorage.getItem("buyer_id");
  });
  const [loading, setLoading] = useState(true);

  // UI-only enhancements (presentation; no backend impact)
  const [dense, setDense] = useState(false);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const handleRowClick = (id) => {
    const newId = buyerId === id ? null : id;
    setBuyerId(newId);
    if (newId) {
      localStorage.removeItem("buyer_id");
    } else {
      localStorage.removeItem("buyer_id");
    }
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected + 1;
    setPage(selectedPage);
    NewRegistrationData(selectedPage); // fetch new page data
  };

  const NewRegistrationData = (pageNumber = 1) => {
    const params = new URLSearchParams();

    if (myOrderFilter && myOrderFilter !== "All") {
      params.append("user_type", myOrderFilter);
    }

    if (search) {
      params.append("search", search);
    }

    // Was never sent at all — the "Select Month" dropdown here did nothing.
    if (monthFilter) {
      params.append("month", monthFilter);
    }

    params.append("page", pageNumber); // send current page
    params.append("limit", 10); // optional: set items per page

    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_URL}/admin/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        const responseData = res?.data?.data.users;
        const filtered = responseData.filter(
          (item) => item.user_type !== "subadmin"
        );
        // Was reading `.pagination` off the filtered ARRAY (always
        // undefined), so this table's pagination silently stuck on page 1
        // no matter how many results actually existed. Pagination lives on
        // the response object, not the users array.
        setTotalPage(res?.data?.data?.pagination?.totalPages || 1);
        setData(filtered || []);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        } else {
          console.error(
            "Error fetching users:",
            err.response?.data || err.message
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const exportToExcel = async () => {
    const params = new URLSearchParams();

    if (myOrderFilter && myOrderFilter !== "All") {
      params.append("user_type", myOrderFilter);
    }
    if (search) params.append("search", search);
    if (monthFilter) params.append("month", monthFilter);

    params.append("page", 1);
    params.append("limit", 10000);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/admin/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      const exportData = response?.data?.data?.users || [];

      if (exportData.length === 0) {
        alert("No data to export.");
        return;
      }

      // Format data for export
      const formattedData = exportData.map((user, index) => {
        const addr = user?.addresses?.[0] || {};
        return {
          "Sr. No.": index + 1,
          Name: user?.name || "",
          Email: user?.email || "",
          Phone: user?.phone || "",
          Company: user?.company_name || "",
          GST: user?.gst_number || "",
          "User Type": user?.user_type || "",
          Status: user?.is_active ? "Active" : "Inactive",
          "Created At": new Date(user?.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          Country: addr.country || "",
          State: addr.state || "",
          City: addr.city || "",
          Pincode: addr.pincode || "",
          Address: addr.address || "",
        };
      });

      // Extract headers
      const header = Object.keys(formattedData[0]);

      // Create worksheet without headers first
      const worksheet = XLSX.utils.json_to_sheet(formattedData, {
        skipHeader: true,
        origin: "A2",
      });

      // Add custom header row
      XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });

      // Set column widths
      worksheet["!cols"] = header.map((h) => ({
        wch: Math.max(h.length + 2, 15),
      }));

      // === Styling ===
      // Header styling (row 1)
      header.forEach((col, i) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: i })];
        if (cell) {
          cell.s = {
            fill: { fgColor: { rgb: "ADD8E6" } }, // Light blue background
            font: { bold: true, color: { rgb: "000000" } }, // Black bold text
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      });

      // Alternate row striping
      formattedData.forEach((_, rowIndex) => {
        if (rowIndex % 2 === 1) {
          header.forEach((_, colIndex) => {
            const cell =
              worksheet[
                XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex })
              ];
            if (cell) {
              cell.s = {
                fill: { fgColor: { rgb: "F2F2F2" } }, // Light gray background
                alignment: { horizontal: "left", vertical: "center" },
              };
            }
          });
        }
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "New Registrations");

      // Write Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(fileData, "new-registration.xlsx");
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export data.");
    }
  };

  useEffect(() => {
    setPage(1); // reset to first page on filter/search change
    NewRegistrationData(1);
  }, [search, myOrderFilter, monthFilter]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const handleViewDetails = (item) => {
    const address = item?.address || {};

    Navigate("/admin/new-registration/show", {
      state: {
        id: item?.user_id,
        name: item?.name,
        company_name: item?.company_name,
        designation: item?.designation,
        status: item?.is_active,
        email: item?.email,
        created_at: item?.created_at,
        gst_number: item?.gst_number,
        phone: item?.phone,
        user_type: item?.user_type,
        machine_configuration: item?.machine_configuration,
        website: item?.website,
        business_type: item?.business_type,

        // 👇 include all address fields
        address: address.address || "",
        city: address.city || "",
        state: address.state || "",
        country: address.country || "",
        pincode: address.pincode || "",

        user_bank_details_id: item?.bank_details?.user_bank_details_id || "",
        account_holder_name: item?.bank_details?.account_holder_name || "",
        bank_name: item?.bank_details?.bank_name || "",
        account_number: item?.bank_details?.account_number || "",
        ifsc_code: item?.bank_details?.ifsc_code || "",
        branch_name: item?.bank_details?.branch_name || "",
      },
    });
  };

  // Tabs map to the existing user_type filter values (unchanged behaviour)
  const TABS = [
    { label: "All", value: "All" },
    { label: "Buyer", value: "Buyer" },
    { label: "Supplier", value: "Supplier" },
    { label: "Offline Buyer", value: "offline_buyer" },
    { label: "Offline Supplier", value: "offline_supplier" },
  ];

  // Client-side sort of the loaded page (presentation only)
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const displayData = useMemo(() => {
    const rows = [...(data || [])];
    if (sortKey) {
      rows.sort((a, b) => {
        let av;
        let bv;
        if (sortKey === "created_at") {
          av = new Date(a.created_at).getTime() || 0;
          bv = new Date(b.created_at).getTime() || 0;
        } else {
          av = (a[sortKey] || "").toString().toLowerCase();
          bv = (b[sortKey] || "").toString().toLowerCase();
        }
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, sortKey, sortDir]);

  const SortIcon = ({ active }) => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        opacity: active ? 1 : 0.45,
        transform:
          active && sortDir === "desc" ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform .15s ease",
      }}
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );

  return (
    <div className="nr">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Page header */}
      <div className="nr-head">
        <div>
          <h1 className="nr-title">New Registration</h1>
          <p className="nr-sub">
            Review and onboard buyers &amp; suppliers joining the marketplace.
          </p>
        </div>
        {userType === "admin" && (
          <button type="button" className="nr-btn" onClick={exportToExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="nr-tabs">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={`nr-tab ${myOrderFilter === t.value ? "is-active" : ""}`}
            onClick={() => setMyOrderFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table card */}
      <div className="nr-card">
        {/* toolbar */}
        <div className="nr-toolbar">
          <div className="nr-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search by name, company, mobile…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && search !== "") NewRegistrationData();
              }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="nr-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(e.target.value);
                NewRegistrationData();
              }}
            >
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>

          <button
            type="button"
            className="nr-tool"
            onClick={() => setDense((d) => !d)}
            title="Toggle density"
            style={{ marginLeft: "auto" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {/* desktop grid table */}
        {!isMobile ? (
          <div className="nr-tablewrap">
            <div
              className="nr-table"
              style={{ "--nr-row-pad": dense ? "8px" : "13px" }}
            >
              {/* header */}
              <div className="nr-thead">
                <span
                  className="nr-th nr-th--sort"
                  onClick={() => toggleSort("name")}
                >
                  Name <SortIcon active={sortKey === "name"} />
                </span>
                <span className="nr-th">Company Name</span>
                <span className="nr-th">Role</span>
                <span className="nr-th">Mobile Number</span>
                <span
                  className="nr-th nr-th--sort"
                  onClick={() => toggleSort("created_at")}
                >
                  Date &amp; Time <SortIcon active={sortKey === "created_at"} />
                </span>
                <span className="nr-th" style={{ textAlign: "right" }}>
                  Details
                </span>
              </div>

              {/* loading skeleton */}
              {loading &&
                [...Array(8)].map((_, i) => (
                  <div className="nr-row" key={`sk-${i}`}>
                    <div className="nr-id">
                      <span className="nr-skel" style={{ width: 32, height: 32, borderRadius: 9 }} />
                      <span className="nr-skel" style={{ width: 120, height: 12 }} />
                    </div>
                    <span className="nr-skel" style={{ width: 90, height: 12 }} />
                    <span className="nr-skel" style={{ width: 60, height: 18, borderRadius: 6 }} />
                    <span className="nr-skel" style={{ width: 90, height: 12 }} />
                    <span className="nr-skel" style={{ width: 110, height: 12 }} />
                    <span className="nr-skel" style={{ width: 30, height: 30, borderRadius: 8, marginLeft: "auto" }} />
                  </div>
                ))}

              {/* rows */}
              {!loading &&
                displayData.map((item) => {
                  const t = tint(item.user_type);
                  return (
                    <div
                      className={`nr-row ${
                        buyerId === String(item.user_id) ? "is-selected" : ""
                      }`}
                      key={item.user_id}
                      onClick={() => handleRowClick(item.user_id)}
                    >
                      <div className="nr-id">
                        <span
                          className="nr-avatar"
                          style={{ background: t.bg, color: t.color }}
                        >
                          {initialsOf(item.name)}
                        </span>
                        <span className="nr-name">{item.name}</span>
                      </div>
                      <span className="nr-muted nr-ellipsis">
                        {item.company_name || "—"}
                      </span>
                      <span>
                        <span
                          className="nr-pill"
                          style={{ background: t.bg, color: t.color }}
                        >
                          {(item.user_type || "").replaceAll("_", " ")}
                        </span>
                      </span>
                      <span className="nr-mono">{item.phone || "—"}</span>
                      <span className="nr-mono nr-muted">
                        {formatDate(item.created_at)}
                      </span>
                      <div className="nr-actions">
                        <button
                          type="button"
                          className="nr-view"
                          title="View details"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(item);
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* empty */}
              {!loading && displayData.length === 0 && (
                <div className="nr-empty">
                  <div className="nr-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <p className="nr-empty__title">No registrations found</p>
                  <p className="nr-empty__text">
                    Try a different role tab or search.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Mobile card view
          <div className="nr-mobile">
            {loading && (
              <div className="nr-mcard">
                <span className="nr-skel" style={{ width: "60%", height: 14 }} />
                <span className="nr-skel" style={{ width: "40%", height: 12, marginTop: 8 }} />
              </div>
            )}
            {!loading && data?.length !== 0 ? (
              data.map((item) => {
                const t = tint(item.user_type);
                return (
                  <div
                    className={`nr-mcard ${
                      buyerId === String(item.user_id) ? "is-selected" : ""
                    }`}
                    key={item.user_id}
                    onClick={() => handleRowClick(item.user_id)}
                  >
                    <div className="nr-mcard__top">
                      <span
                        className="nr-avatar"
                        style={{ background: t.bg, color: t.color }}
                      >
                        {initialsOf(item.name)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="nr-name">{item.name}</div>
                        <div className="nr-muted nr-ellipsis">
                          {item.company_name || "—"}
                        </div>
                      </div>
                      <span
                        className="nr-pill"
                        style={{ background: t.bg, color: t.color, marginLeft: "auto" }}
                      >
                        {(item.user_type || "").replaceAll("_", " ")}
                      </span>
                    </div>
                    <div className="nr-mrow">
                      <span className="nr-muted">Mobile</span>
                      <span className="nr-mono">{item.phone || "—"}</span>
                    </div>
                    <div className="nr-mrow">
                      <span className="nr-muted">Date</span>
                      <span className="nr-mono nr-muted">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="nr-btn nr-btn--block"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(item);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                );
              })
            ) : (
              !loading && (
                <div className="nr-empty">
                  <p className="nr-empty__title">No Record Found</p>
                </div>
              )
            )}
          </div>
        )}

        {/* pagination */}
        <div className="nr-foot">
          <span className="nr-foot__info">
            Showing <strong>{displayData.length}</strong> registrations
          </span>
          {data?.length !== 0 && (
            <ReactPaginate
              previousLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              }
              nextLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              }
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          )}
        </div>
      </div>

      <NewRegStyles />
    </div>
  );
};

// Scoped styles — all colors come from the global design tokens (themeable).
function NewRegStyles() {
  return (
    <style>{`
.nr{ color: var(--color-text-primary); }
.nr *{ box-sizing:border-box; }

/* page header */
.nr-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.nr-title{ margin:0; font-size:24px; font-weight:800; letter-spacing:-.025em; color:var(--color-text-primary); }
.nr-sub{ margin:5px 0 0; font-size:13.5px; color:var(--color-text-secondary); }
.nr-btn{
  display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px;
  border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary);
  font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; transition:background .15s ease;
}
.nr-btn:hover{ background:var(--color-hover); }
.nr-btn--block{ width:100%; justify-content:center; margin-top:10px; }

/* tabs */
.nr-tabs{ display:flex; align-items:center; gap:4px; border-bottom:1px solid var(--color-border); margin-bottom:16px; overflow-x:auto; }
.nr-tab{
  display:inline-flex; align-items:center; gap:7px; padding:11px 14px; font-size:13px; font-weight:600;
  color:var(--color-text-secondary); background:transparent; border:0; border-bottom:2px solid transparent;
  cursor:pointer; white-space:nowrap; transition:color .15s ease, border-color .15s ease;
}
.nr-tab:hover{ color:var(--color-text-primary); }
.nr-tab.is-active{ color:var(--color-primary); font-weight:700; border-bottom-color:var(--color-primary); }

/* card */
.nr-card{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); overflow:hidden; }

/* toolbar */
.nr-toolbar{ display:flex; align-items:center; gap:10px; padding:13px 16px; border-bottom:1px solid var(--color-border-soft); flex-wrap:wrap; }
.nr-search{ display:flex; align-items:center; gap:9px; height:36px; padding:0 11px; background:var(--color-surface-2); border:1px solid var(--color-border); border-radius:9px; flex:1; min-width:200px; max-width:320px; }
.nr-search input{ border:0; outline:0; background:transparent; font-family:inherit; font-size:12.5px; color:var(--color-text-primary); width:100%; }
.nr-select{ position:relative; display:flex; align-items:center; gap:7px; height:36px; padding:0 11px; border-radius:9px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-secondary); }
.nr-select select{ border:0; outline:0; background:transparent; font-family:inherit; font-size:12.5px; font-weight:600; color:var(--color-text-secondary); cursor:pointer; padding-right:4px; }
.nr-tool{ display:flex; align-items:center; gap:7px; height:36px; padding:0 12px; border-radius:9px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-secondary); font-family:inherit; font-size:12.5px; font-weight:600; cursor:pointer; transition:background .15s ease; }
.nr-tool:hover{ background:var(--color-hover); }

/* grid table */
.nr-tablewrap{ overflow-x:auto; }
.nr-table{ min-width:920px; }
.nr-thead, .nr-row{ display:grid; grid-template-columns:1.6fr 1.3fr .9fr 1fr 1.1fr 70px; gap:12px; align-items:center; }
.nr-thead{ padding:11px 18px; background:var(--color-surface-2); border-bottom:1px solid var(--color-border); position:sticky; top:0; z-index:1; }
.nr-th{ font-size:10.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:var(--color-text-tertiary); display:flex; align-items:center; gap:5px; }
.nr-th--sort{ cursor:pointer; user-select:none; }
.nr-row{ padding:var(--nr-row-pad,13px) 18px; border-bottom:1px solid var(--color-border-soft); transition:background .12s ease; cursor:pointer; }
.nr-row:last-child{ border-bottom:none; }
.nr-row:hover{ background:var(--color-hover); }
.nr-row.is-selected{ background:var(--color-primary-weak); }
.nr-id{ display:flex; align-items:center; gap:11px; min-width:0; }
.nr-avatar{ width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:11.5px; font-weight:700; flex:0 0 32px; }
.nr-name{ font-size:13px; font-weight:700; color:var(--color-text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.nr-muted{ color:var(--color-text-secondary); font-size:12.5px; }
.nr-ellipsis{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.nr-mono{ font-family:var(--font-mono); font-size:12px; color:var(--color-text-secondary); }
.nr-pill{ display:inline-flex; align-items:center; font-size:11px; font-weight:700; border-radius:6px; padding:3px 9px; text-transform:capitalize; white-space:nowrap; }
.nr-actions{ display:flex; align-items:center; justify-content:flex-end; gap:4px; }
.nr-view{ width:30px; height:30px; border-radius:8px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-secondary); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s ease; }
.nr-view:hover{ background:var(--color-primary-weak); color:var(--color-primary); border-color:var(--color-primary); }

/* skeleton */
.nr-skel{ display:inline-block; background:linear-gradient(90deg, var(--color-hover) 25%, var(--color-surface-2) 37%, var(--color-hover) 63%); background-size:400% 100%; animation:nrShimmer 1.3s infinite; border-radius:6px; }
@keyframes nrShimmer{ 0%{ background-position:100% 0; } 100%{ background-position:-100% 0; } }

/* empty */
.nr-empty{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:48px 20px; text-align:center; }
.nr-empty__icon{ width:54px; height:54px; border-radius:50%; background:var(--color-surface-2); color:var(--color-text-tertiary); display:flex; align-items:center; justify-content:center; }
.nr-empty__title{ margin:0; font-size:15px; font-weight:700; color:var(--color-text-primary); }
.nr-empty__text{ margin:0; font-size:13px; color:var(--color-text-secondary); }

/* mobile cards */
.nr-mobile{ padding:14px; display:flex; flex-direction:column; gap:12px; }
.nr-mcard{ border:1px solid var(--color-border); border-radius:12px; padding:14px; background:var(--color-surface); cursor:pointer; transition:background .12s ease; }
.nr-mcard.is-selected{ background:var(--color-primary-weak); border-color:var(--color-primary); }
.nr-mcard__top{ display:flex; align-items:center; gap:11px; }
.nr-mrow{ display:flex; align-items:center; justify-content:space-between; margin-top:8px; font-size:12.5px; }

/* footer / pagination */
.nr-foot{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 18px; border-top:1px solid var(--color-border); flex-wrap:wrap; }
.nr-foot__info{ font-size:12px; color:var(--color-text-secondary); }
.nr-foot__info strong{ color:var(--color-text-primary); }
.nr-foot .pagination{ margin:0; }
    `}</style>
  );
}

export default NewRegistration;
